import React, { useMemo, useCallback, useEffect } from 'react';
// v10.0.7 - Fixed dynamic imports - 2026-03-12
import { useSearchParams } from 'react-router';
import { Logo } from './components/Logo';
import { TrafficFeed, TrafficReport } from './components/TrafficFeed';
import { TransportWatch } from './components/TransportWatch';
import { WeatherWidget } from './components/WeatherWidget';
import { EventList, Event } from './components/EventList';
import { FloodFeed, FloodReport } from './components/FloodFeed';
import { MapPreview } from './components/MapPreview';
import { AuthModal } from './components/AuthModal';
import { CreatePostModal } from './components/CreatePostModal';
import { DevTools } from './components/DevTools';
import { OTPModal } from './components/OTPModal';
import { HowItWorksModal } from './components/HowItWorksModal';
import { WelcomeModal } from './components/WelcomeModal';
import { BusinessAuthModal } from './components/BusinessAuthModal';
import { BusinessDashboard } from './components/BusinessDashboard';
import { GoogleMapView } from './components/GoogleMapView';
import { SOSModal } from './components/SOSModal';
import { PartnershipHub } from './components/PartnershipHub';
import { MerchantQRScanner, NeighborhoodGrantsWidget, MerchantTerminal } from './components/HiddenFeatures';
import { LightWatchWidget } from './components/PowerTracker';
import { AjoSavingsWidget } from './components/AjoSavings';
import { MarketPriceIndex } from './components/MarketPriceIndex';
import { VoiceReporter } from './components/VoiceReporter';
import { TransportFuelHub } from './components/TransportFuelHub';
import { NeighborhoodMarketplace } from './components/NeighborhoodMarketplace';
import { BusStopCrowdWatch } from './components/BusStopCrowdWatch';
import { AdminTerminal } from './components/AdminTerminal';
import { SettingsModal, NotificationPreferences } from './components/SettingsModal';
import { SecurityCenter } from './components/SecurityCenter';
import { PushAlert } from './components/PushAlert';
import { BottomNav } from './components/BottomNav';
import { ShareModal } from './components/ShareModal';
import { SignupTester } from './components/SignupTester';
import { SupabaseDiagnostic } from './components/SupabaseDiagnostic';
import { SimpleOnboarding } from './components/SimpleOnboarding';
import { QuickAccessMenu } from './components/QuickAccessMenu';
import { ReferralDashboard } from './components/ReferralDashboard';
import { AdminControlPanel } from './components/AdminControlPanel';
import { AdvertBanner } from './components/AdvertBanner';
import { HelpButton } from './components/HelpButton';
import { FloatingFeatureMenu } from './components/FloatingFeatureMenu';
import { MyPostsView } from './components/MyPostsView';
import { UserProfileView } from './components/UserProfileView';
import { NotificationCenter } from './components/NotificationCenter';
import { ReportDetailModal } from './components/ReportDetailModal';
import { EditPostModal } from './components/EditPostModal';
import { LocationSearchHeader } from './components/LocationSearchHeader';
import { ReportCard } from './components/ReportCard';
import { RepostModal } from './components/RepostModal';
import { DUMMY_REPORTS } from './data/mockData';
import { supabase } from './utils/supabase-client';
import { recordDeviceLogin, getDeviceFingerprint } from './utils/device-tracking';
import { detectCountry, getCurrency as formatCurrency } from './utils/country-utils';
import { getUserLocation, formatLocation } from './utils/location-utils';
import { copyToClipboard } from './utils/clipboard';
import { reportsService, authService } from './services/api';
import { isFeatureEnabled } from './utils/feature-flags';
import { 
  Bell, Search, Map as MapIcon, MapPin, Calendar, CloudSun, Sun, CloudRain, Moon, RefreshCw, Plus, Menu, UserCircle, LogOut, Camera,
  ShieldCheck, ShieldAlert, BadgeCheck, Lock, HelpCircle, Fuel, ShoppingBag, Mic, Settings,
  TrendingUp, TrendingDown, Minus, Building2, Zap, ArrowRight, LayoutDashboard,
  Trophy, Sparkles, Gift, Coins, Users, X, AlertTriangle, Send, Link as LinkIcon, Share2, ExternalLink,
  Globe, ThumbsUp, MessageCircle, Clock, TrendingUpIcon, Eye, EyeOff, CheckCircle, Info, Droplets, Navigation2, ChevronDown, ChevronUp, Target, Trash2
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { PWAUpdateNotification } from './components/PWAUpdateNotification';
import { VersionManager } from './components/VersionManager';
import { OfflineIndicator } from './components/OfflineIndicator';
import { InstallAppButton } from './components/InstallAppButton';
import { AppHealthCheck } from './components/AppHealthCheck';

interface Report {
  id: string;
  type: string;
  content: string;
  location: string;
  timestamp: string;
  userName: string;
  userAvatar?: string;
  likes: number;
  comments: number | any[]; // Support both number and array of comments
  verified?: boolean;
  severity?: string;
  status?: string;
  price?: number;
  imageUrl?: string;
  userId?: string;
  country?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  country: string;
  role: 'user' | 'admin' | 'business' | 'agent';
  trustScore: number;
  coins: number;
  referralCode?: string;
  createdAt: string;
}

type ViewType = 'home' | 'community' | 'feature' | 'hub' | 'map' | 'profile' | 'search';
type TabType = 'traffic' | 'events' | 'fuel' | 'market' | 'power' | 'flood' | 'find' | 'nuisance' | 'water';

export function MainApp() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // User state
  const [user, setUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  
  // View and navigation state
  const [activeView, setActiveView] = React.useState<ViewType>('home');
  const [activeTab, setActiveTab] = React.useState<TabType>('traffic');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchHistory, setSearchHistory] = React.useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = React.useState('all');
  
  // Modal states
  const [showAuthModal, setShowAuthModal] = React.useState(false);
  const [showCreatePost, setShowCreatePost] = React.useState(false);
  const [showSettings, setShowSettings] = React.useState(false);
  const [showOTPModal, setShowOTPModal] = React.useState(false);
  const [showHowItWorks, setShowHowItWorks] = React.useState(false);
  const [showWelcome, setShowWelcome] = React.useState(false);
  const [showBusinessAuth, setShowBusinessAuth] = React.useState(false);
  const [showSOSModal, setShowSOSModal] = React.useState(false);
  const [showShareModal, setShowShareModal] = React.useState(false);
  const [showOnboarding, setShowOnboarding] = React.useState(false);
  const [showAdminPanel, setShowAdminPanel] = React.useState(false);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [showReportDetail, setShowReportDetail] = React.useState(false);
  const [showEditPost, setShowEditPost] = React.useState(false);
  const [showMyPosts, setShowMyPosts] = React.useState(false);
  const [showUserProfile, setShowUserProfile] = React.useState(false);
  const [showReferralDashboard, setShowReferralDashboard] = React.useState(false);
  const [showRepostModal, setShowRepostModal] = React.useState(false);
  const [repostingPost, setRepostingPost] = React.useState<any>(null);
  
  // Notification preferences state
  const [preferences, setPreferences] = React.useState<NotificationPreferences>({
    traffic: true,
    weather: true,
    market: true,
    sos: true,
    soundEnabled: true,
    biometricsEnabled: false,
    loginAlerts: true,
    pinEnabled: false,
    walletLock: false,
    ghostMode: false,
    privateProfile: false,
    anonymousReporting: true,
    trustedDevices: [],
    soundType: 'default',
    methods: {
      push: true,
      email: false,
      sms: false,
      whatsapp: false
    }
  });
  
  // Selected items
  const [selectedReport, setSelectedReport] = React.useState<Report | null>(null);
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
  const [itemToShare, setItemToShare] = React.useState<any>(null);
  
  // Admin mode states
  const [isAdminDeleteMode, setIsAdminDeleteMode] = React.useState(false);
  const [selectedPostIds, setSelectedPostIds] = React.useState<Set<string>>(new Set());
  
  // Feature states
  const [showPowerTracker, setShowPowerTracker] = React.useState(false);
  const [showAjoSavings, setShowAjoSavings] = React.useState(false);
  const [showMarketPrices, setShowMarketPrices] = React.useState(false);
  const [showVoiceReporter, setShowVoiceReporter] = React.useState(false);
  const [showTransportHub, setShowTransportHub] = React.useState(false);
  const [showMarketplace, setShowMarketplace] = React.useState(false);
  const [showBusStopWatch, setShowBusStopWatch] = React.useState(false);
  const [showPartnershipHub, setShowPartnershipHub] = React.useState(false);
  const [showSecurityCenter, setShowSecurityCenter] = React.useState(false);
  
  // Feature flags
  const [isAjoEnabled, setIsAjoEnabled] = React.useState(() => isFeatureEnabled('digitalAjo'));
  
  // Data states
  const [trafficReports, setTrafficReports] = React.useState<TrafficReport[]>([]);
  const [events, setEvents] = React.useState<Event[]>([]);
  const [fuelUpdates, setFuelUpdates] = React.useState<any[]>([]);
  const [marketPrices, setMarketPrices] = React.useState<any[]>([]);
  const [powerReports, setPowerReports] = React.useState<any[]>([]);
  const [floodReports, setFloodReports] = React.useState<FloodReport[]>([]);
  const [findReports, setFindReports] = React.useState<any[]>([]);
  const [nuisanceReports, setNuisanceReports] = React.useState<any[]>([]);
  const [waterVendors, setWaterVendors] = React.useState<any[]>([]);
  
  // Track if initial data load is complete (prevents saving empty arrays on mount)
  const [isDataLoaded, setIsDataLoaded] = React.useState(false);
  
  // Location state
  const [userLocation, setUserLocation] = React.useState<{ city?: string; state?: string; country?: string; countryCode?: string; latitude?: number; longitude?: number } | null>(null);
  const [detectedCountry, setDetectedCountry] = React.useState<string>('Nigeria');
  
  // Sync state
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [lastSyncTime, setLastSyncTime] = React.useState<Date | null>(null);
  
  // OTP state
  const [pendingEmail, setPendingEmail] = React.useState('');
  const [pendingPassword, setPendingPassword] = React.useState('');
  const [pendingName, setPendingName] = React.useState('');

  // Get all reports combined
  const allReports = React.useMemo(() => {
    console.log('🔄 Recomputing allReports. Traffic count:', trafficReports.length);
    const combined = [
      ...trafficReports,
      ...events,
      ...fuelUpdates,
      ...marketPrices,
      ...powerReports,
      ...floodReports,
      ...findReports,
      ...nuisanceReports,
      ...waterVendors
    ];
    
    // Remove duplicates by ID - keep the first occurrence (most recent)
    const seen = new Set<string>();
    const deduplicated = combined.filter(report => {
      if (!report.id) return true; // Keep reports without ID
      if (seen.has(report.id)) return false; // Skip duplicates
      seen.add(report.id);
      return true;
    });
    
    console.log('✅ All reports combined. Total:', deduplicated.length, '| Traffic:', trafficReports.length);
    return deduplicated;
  }, [trafficReports, events, fuelUpdates, marketPrices, powerReports, floodReports, findReports, nuisanceReports, waterVendors]);

  // Initialize app - check auth and load data
  React.useEffect(() => {
    initializeApp();
  }, []);

  // Listen for feature flag changes
  React.useEffect(() => {
    const handleFlagChange = () => {
      setIsAjoEnabled(isFeatureEnabled('digitalAjo'));
    };
    
    window.addEventListener('featureFlagsChanged', handleFlagChange);
    return () => window.removeEventListener('featureFlagsChanged', handleFlagChange);
  }, []);

  // Periodic backend sync to fetch new posts from other users
  React.useEffect(() => {
    // Sync every 60 seconds to get new posts from the community
    const interval = setInterval(() => {
      console.log('⏰ Periodic sync - fetching latest posts from backend...');
      syncWithBackend();
    }, 60000); // 60 seconds
    
    return () => clearInterval(interval);
  }, [detectedCountry]);

  // Handle shared report links - open post detail modal automatically
  React.useEffect(() => {
    const sharedReportId = searchParams.get('sharedReport');
    
    if (sharedReportId) {
      console.log('🔗 Detected shared report link:', sharedReportId);
      
      // Wait for data to load
      setTimeout(() => {
        // Find the report in any of the feeds
        const allReports = [
          ...trafficReports,
          ...events,
          ...fuelUpdates,
          ...marketPrices,
          ...powerReports,
          ...floodReports,
          ...findReports,
          ...nuisanceReports,
          ...waterVendors
        ];
        
        const report = allReports.find(r => r.id === sharedReportId);
        
        if (report) {
          console.log('✅ Found shared report, opening detail modal');
          setSelectedReport(report);
          setShowReportDetail(true);
          
          // Switch to the correct tab based on report type
          const reportType = searchParams.get('reportType') || report.type;
          if (reportType === 'traffic' || reportType === 'road') {
            setActiveTab('traffic');
          } else if (reportType === 'event') {
            setActiveTab('events');
          } else if (reportType === 'fuel') {
            setActiveTab('fuel');
          } else if (reportType === 'flood' || reportType === 'water') {
            setActiveTab('flood');
          }
          
          // Show success toast
          toast.success('Opened shared report!', {
            description: 'Viewing community update'
          });
          
          // Clean up URL (remove query params)
          window.history.replaceState({}, '', window.location.pathname);
        } else {
          console.log('⚠️ Shared report not found, it may have been deleted');
          toast.info('This report may have been removed', {
            description: 'Showing latest community updates instead'
          });
          
          // Clean up URL
          window.history.replaceState({}, '', window.location.pathname);
        }
      }, 1500); // Wait for backend sync
    }
  }, [searchParams, trafficReports, events, fuelUpdates, marketPrices, powerReports, floodReports, findReports, nuisanceReports, waterVendors]);

  const initializeApp = async () => {
    try {
      // Check for existing session
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (session?.user) {
        await loadUserProfile(session.user.id);
      }
      
      // Detect country
      const country = await detectCountry();
      setDetectedCountry(country);
      
      // Load location - non-blocking, don't fail initialization if it errors
      try {
        const location = await getUserLocation();
        setUserLocation(location);
      } catch (locationError) {
        console.log('📍 Location detection failed, using defaults');
        // Set default location without showing error
        setUserLocation({ 
          city: 'Lagos', 
          state: 'Lagos', 
          country: 'Nigeria',
          countryCode: 'NG'
        });
      }
      
      // Check if first time user - show onboarding only once to new users
      try {
        const hasSeenOnboarding = localStorage.getItem('sentlo_has_seen_onboarding');
        if (hasSeenOnboarding !== 'true') {
          setShowOnboarding(true);
        }
      } catch (storageError) {
        console.log('⚠️ localStorage check failed, skipping onboarding check');
      }
      
      // Load data from localStorage first (instant)
      loadFromLocalStorage();
      
      // Then sync with backend after 1 second
      setTimeout(() => {
        syncWithBackend();
      }, 1000);
      
    } catch (error) {
      console.error('❌ Initialization error:', error);
      // Don't show error toast if it's just a location permission issue
      if (error instanceof Error && !error.message.includes('location')) {
        toast.error('Failed to initialize app. Please refresh the page.');
      }
    } finally {
      // Always set loading to false to prevent white screen
      setIsLoading(false);
    }
  };

  const loadUserProfile = async (userId: string) => {
    try {
      const userData = await authService.getUserProfile(userId);
      if (userData) {
        setUser(userData);
        console.log('✅ User profile loaded:', userData.name);
      } else {
        console.log('📡 No user profile found (expected for new users)');
      }
    } catch (error: any) {
      // Silently handle errors - profile fetch is not critical
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        console.log('📡 Backend unavailable - working offline');
      } else {
        console.error('❌ Profile load error:', error);
      }
    }
  };

  // Load data from localStorage
  const loadFromLocalStorage = () => {
    try {
      console.log('🔄 loadFromLocalStorage() called');
      console.log('📦 DUMMY_REPORTS available:', DUMMY_REPORTS.length);
      
      const traffic = localStorage.getItem('sentlo_traffic_reports');
      const eventsData = localStorage.getItem('sentlo_events');
      const fuel = localStorage.getItem('sentlo_fuel_updates');
      const market = localStorage.getItem('sentlo_market_prices');
      const power = localStorage.getItem('sentlo_power_reports');
      const flood = localStorage.getItem('sentlo_flood_reports');
      const find = localStorage.getItem('sentlo_find_reports');
      const nuisance = localStorage.getItem('sentlo_nuisance_reports');
      const water = localStorage.getItem('sentlo_water_vendors');
      
      console.log('📍 localStorage traffic:', traffic ? traffic.substring(0, 50) : 'null');
      
      // Load from localStorage if available AND not empty, otherwise use mock data
      const trafficData = traffic ? JSON.parse(traffic) : [];
      const eventsDataParsed = eventsData ? JSON.parse(eventsData) : [];
      const fuelData = fuel ? JSON.parse(fuel) : [];
      const marketData = market ? JSON.parse(market) : [];
      const powerData = power ? JSON.parse(power) : [];
      const floodData = flood ? JSON.parse(flood) : [];
      const findData = find ? JSON.parse(find) : [];
      const nuisanceData = nuisance ? JSON.parse(nuisance) : [];
      const waterData = water ? JSON.parse(water) : [];
      
      console.log('📊 Parsed counts - localStorage:', {
        traffic: trafficData.length,
        events: eventsDataParsed.length,
        power: powerData.length
      });
      
      const trafficMock = DUMMY_REPORTS.filter(r => r.type === 'traffic');
      const eventsMock = DUMMY_REPORTS.filter(r => r.type === 'event');
      const powerMock = DUMMY_REPORTS.filter(r => r.type === 'power');
      
      console.log('📊 Mock data counts:', {
        trafficMock: trafficMock.length,
        eventsMock: eventsMock.length,
        powerMock: powerMock.length
      });
      
      console.log('🎯 Will use:', {
        traffic: trafficData.length > 0 ? 'localStorage' : 'MOCK DATA',
        events: eventsDataParsed.length > 0 ? 'localStorage' : 'MOCK DATA',
        power: powerData.length > 0 ? 'localStorage' : 'MOCK DATA'
      });
      
      setTrafficReports(trafficData.length > 0 ? trafficData : trafficMock);
      setEvents(eventsDataParsed.length > 0 ? eventsDataParsed : eventsMock);
      setFuelUpdates(fuelData.length > 0 ? fuelData : DUMMY_REPORTS.filter(r => r.type === 'fuel'));
      setMarketPrices(marketData.length > 0 ? marketData : DUMMY_REPORTS.filter(r => r.type === 'market'));
      setPowerReports(powerData.length > 0 ? powerData : powerMock);
      setFloodReports(floodData.length > 0 ? floodData : DUMMY_REPORTS.filter(r => r.type === 'flood'));
      setFindReports(findData.length > 0 ? findData : DUMMY_REPORTS.filter(r => r.type === 'find'));
      setNuisanceReports(nuisanceData.length > 0 ? nuisanceData : DUMMY_REPORTS.filter(r => r.type === 'nuisance'));
      setWaterVendors(waterData.length > 0 ? waterData : DUMMY_REPORTS.filter(r => r.type === 'water'));
      
      // Mark data as loaded to allow localStorage saving
      setIsDataLoaded(true);
      
      console.log('✅ DATA LOAD COMPLETE - Check trafficReports state after this');
      console.log(`📊 Total mock posts available: ${DUMMY_REPORTS.length} | Traffic: ${trafficMock.length}, Power: ${powerMock.length}`);
    } catch (error) {
      console.error('❌ Failed to load from localStorage:', error);
      // If there's an error, still load mock data as fallback
      setTrafficReports(DUMMY_REPORTS.filter(r => r.type === 'traffic'));
      setEvents(DUMMY_REPORTS.filter(r => r.type === 'event'));
      setFuelUpdates(DUMMY_REPORTS.filter(r => r.type === 'fuel'));
      setMarketPrices(DUMMY_REPORTS.filter(r => r.type === 'market'));
      setPowerReports(DUMMY_REPORTS.filter(r => r.type === 'power'));
      setFloodReports(DUMMY_REPORTS.filter(r => r.type === 'flood'));
      setNuisanceReports(DUMMY_REPORTS.filter(r => r.type === 'nuisance'));
    }
  };

  // Save to localStorage whenever data changes (but only AFTER initial load)
  React.useEffect(() => {
    if (!isDataLoaded) return; // Don't save empty arrays on mount
    try {
      localStorage.setItem('sentlo_traffic_reports', JSON.stringify(trafficReports));
    } catch (error) {
      console.error('❌ Failed to save traffic reports to localStorage:', error);
    }
  }, [trafficReports, isDataLoaded]);

  React.useEffect(() => {
    if (!isDataLoaded) return;
    try {
      localStorage.setItem('sentlo_events', JSON.stringify(events));
    } catch (error) {
      console.error('❌ Failed to save events to localStorage:', error);
    }
  }, [events, isDataLoaded]);

  React.useEffect(() => {
    if (!isDataLoaded) return;
    try {
      localStorage.setItem('sentlo_fuel_updates', JSON.stringify(fuelUpdates));
    } catch (error) {
      console.error('❌ Failed to save fuel updates to localStorage:', error);
    }
  }, [fuelUpdates, isDataLoaded]);

  React.useEffect(() => {
    if (!isDataLoaded) return;
    try {
      localStorage.setItem('sentlo_market_prices', JSON.stringify(marketPrices));
    } catch (error) {
      console.error('❌ Failed to save market prices to localStorage:', error);
    }
  }, [marketPrices, isDataLoaded]);

  React.useEffect(() => {
    if (!isDataLoaded) return;
    try {
      localStorage.setItem('sentlo_power_reports', JSON.stringify(powerReports));
    } catch (error) {
      console.error('❌ Failed to save power reports to localStorage:', error);
    }
  }, [powerReports, isDataLoaded]);

  React.useEffect(() => {
    if (!isDataLoaded) return;
    try {
      localStorage.setItem('sentlo_flood_reports', JSON.stringify(floodReports));
    } catch (error) {
      console.error('❌ Failed to save flood reports to localStorage:', error);
    }
  }, [floodReports, isDataLoaded]);

  React.useEffect(() => {
    if (!isDataLoaded) return;
    try {
      localStorage.setItem('sentlo_find_reports', JSON.stringify(findReports));
    } catch (error) {
      console.error('❌ Failed to save find reports to localStorage:', error);
    }
  }, [findReports, isDataLoaded]);

  React.useEffect(() => {
    if (!isDataLoaded) return;
    try {
      localStorage.setItem('sentlo_nuisance_reports', JSON.stringify(nuisanceReports));
    } catch (error) {
      console.error('❌ Failed to save nuisance reports to localStorage:', error);
    }
  }, [nuisanceReports, isDataLoaded]);

  React.useEffect(() => {
    if (!isDataLoaded) return;
    try {
      localStorage.setItem('sentlo_water_vendors', JSON.stringify(waterVendors));
    } catch (error) {
      console.error('❌ Failed to save water vendors to localStorage:', error);
    }
  }, [waterVendors, isDataLoaded]);

  // DIAGNOSTIC: Log when trafficReports state changes
  React.useEffect(() => {
    console.log('🔔 trafficReports state changed, count:', trafficReports.length);
    if (trafficReports.length > 0) {
      console.log('📍 First traffic report:', trafficReports[0]);
    }
  }, [trafficReports]);

  // Merge function that preserves local and recent posts
  const mergeReports = (localReports: any[], backendReports: any[]) => {
    // If backend is empty, keep ALL local reports (including mock data)
    if (backendReports.length === 0) {
      console.log('📦 Backend empty, keeping all local reports:', localReports.length);
      return localReports;
    }
    
    const backendIds = new Set(backendReports.map(r => r.id));
    const localOnly = localReports.filter(r => {
      // Always keep temp ID posts
      if (r.id && r.id.startsWith('local-')) {
        console.log('🔄 Keeping local post:', r.id, '| timestamp:', r.timestamp);
        return true;
      }
      
      // Keep recent posts that aren't in backend yet
      if (!backendIds.has(r.id)) {
        const isRecent = r.timestamp === 'Just now' || 
                         (r.timestamp && r.timestamp.includes('min ago'));
        if (isRecent) {
          console.log('🔄 Keeping recent post:', r.id, '| timestamp:', r.timestamp);
          return true;
        }
      }
      
      return false;
    });
    
    console.log(`📊 Merge result: ${localOnly.length} local-only + ${backendReports.length} backend = ${localOnly.length + backendReports.length} total (before dedup)`);
    
    // Combine with local posts FIRST (so they appear at the top)
    const combined = [...localOnly, ...backendReports];
    const seen = new Set<string>();
    return combined.filter(report => {
      if (!report.id) return true;
      if (seen.has(report.id)) return false;
      seen.add(report.id);
      return true;
    });
  };

  // Sync with backend
  const syncWithBackend = async () => {
    if (!detectedCountry) return;
    
    try {
      setIsSyncing(true);
      console.log('🔄 Syncing with backend for country:', detectedCountry);
      
      const backendData = await reportsService.getReportsByCountry(detectedCountry);
      
      if (backendData && backendData.reports) {
        console.log('📡 Backend returned', backendData.reports.length, 'reports');
        
        // Group reports by type
        const traffic = backendData.reports.filter((r: any) => r.type === 'traffic');
        const events = backendData.reports.filter((r: any) => r.type === 'event');
        const fuel = backendData.reports.filter((r: any) => r.type === 'fuel');
        const market = backendData.reports.filter((r: any) => r.type === 'market');
        const power = backendData.reports.filter((r: any) => r.type === 'power');
        const flood = backendData.reports.filter((r: any) => r.type === 'flood');
        const find = backendData.reports.filter((r: any) => r.type === 'find');
        const nuisance = backendData.reports.filter((r: any) => r.type === 'nuisance');
        const water = backendData.reports.filter((r: any) => r.type === 'water');
        
        console.log('📊 Backend data by type:', { traffic: traffic.length, events: events.length, power: power.length });
        
        // Merge backend data with local data (preserves local-only posts)
        console.log('🔄 Merging backend data with local data...');
        setTrafficReports(prev => {
          const merged = mergeReports(prev, traffic);
          console.log(`📊 Traffic: ${prev.length} local → ${merged.length} after merge`);
          return merged;
        });
        setEvents(prev => {
          const merged = mergeReports(prev, events);
          console.log(`📊 Events: ${prev.length} local → ${merged.length} after merge`);
          return merged;
        });
        setFuelUpdates(prev => mergeReports(prev, fuel));
        setMarketPrices(prev => mergeReports(prev, market));
        setPowerReports(prev => {
          const merged = mergeReports(prev, power);
          console.log(`📊 Power: ${prev.length} local → ${merged.length} after merge`);
          return merged;
        });
        setFloodReports(prev => mergeReports(prev, flood));
        setFindReports(prev => mergeReports(prev, find));
        setNuisanceReports(prev => mergeReports(prev, nuisance));
        setWaterVendors(prev => mergeReports(prev, water));
        
        setLastSyncTime(new Date());
        console.log('✅ Backend sync complete');
      } else {
        console.log('📡 Backend returned no data, keeping local data');
      }
    } catch (error: any) {
      // Silently handle network errors - they're expected when offline or backend unavailable
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError') ||
          error?.message?.includes('network')) {
        console.log('📡 Backend unavailable - working offline');
      } else {
        console.error('❌ Backend sync failed:', error);
      }
      // Don't show error toast - app works offline
    } finally {
      setIsSyncing(false);
    }
  };

  // Auto-retry sync for local posts every 30 seconds
  React.useEffect(() => {
    const retrySyncLocalPosts = async () => {
      const localPosts = allReports.filter(r => r.id?.startsWith('local-'));
      
      if (localPosts.length === 0) return;
      
      console.log(`🔄 Found ${localPosts.length} local posts to sync`);
      
      for (const post of localPosts) {
        try {
          console.log(`🔄 Attempting to sync post ${post.id}...`);
          
          // Get current session
          const { data: { session } } = await supabase.auth.getSession();
          
          if (!session) {
            console.log('⚠️ No active session - skipping sync');
            continue;
          }
          
          // Sync to backend
          const response = await reportsService.createReport(post);
          
          if (response && response.id) {
            console.log(`✅ Successfully synced post ${post.id} with backend ID ${response.id}`);
            
            // Update the post ID in the appropriate state
            const updatePostId = (reports: any[]) => 
              reports.map(r => r.id === post.id ? { ...r, id: response.id } : r);
            
            if (post.type === 'traffic') setTrafficReports(updatePostId);
            else if (post.type === 'event') setEvents(updatePostId);
            else if (post.type === 'fuel') setFuelUpdates(updatePostId);
            else if (post.type === 'market') setMarketPrices(updatePostId);
            else if (post.type === 'power') setPowerReports(updatePostId);
            else if (post.type === 'flood') setFloodReports(updatePostId);
            else if (post.type === 'find') setFindReports(updatePostId);
            else if (post.type === 'nuisance') setNuisanceReports(updatePostId);
            else if (post.type === 'water') setWaterVendors(updatePostId);
            
            toast.success('Post Synced to Cloud!');
          }
        } catch (error: any) {
          // Silently handle network errors - they're expected when offline
          if (error?.message?.includes('Failed to fetch') || 
              error?.message?.includes('NetworkError') ||
              error?.message?.includes('network') ||
              error?.name === 'TypeError') {
            console.log(`📡 Network unavailable for post ${post.id} - will retry later`);
          } else {
            // Log other errors for debugging
            console.error(`❌ Failed to sync post ${post.id}:`, error);
          }
          // Will retry on next interval
        }
      }
    };
    
    // Initial retry after 5 seconds
    const timer = setTimeout(retrySyncLocalPosts, 5000);
    
    // Then every 30 seconds
    const interval = setInterval(retrySyncLocalPosts, 30000);
    
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [allReports, user]);

  // Handle post creation
  const handleCreatePost = async (postData: any) => {
    try {
      console.log('📝 Creating new post:', postData);
      
      // Generate temp ID
      const tempId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Create post object
      const newPost = {
        ...postData,
        id: tempId,
        userId: user?.id || 'anonymous',
        userName: user?.name || 'Anonymous',
        userAvatar: user?.avatar,
        timestamp: 'Just now',
        likes: 0,
        comments: [], // Initialize as empty array for comments
        country: detectedCountry,
        location: postData.location || postData.lga || userLocation?.city || 'Unknown',
        content: postData.content || postData.description, // Ensure content field exists
        description: postData.description || postData.content // Backward compatibility
      };
      
      console.log('✅ Adding post to UI with temp ID:', tempId);
      console.log('📍 Post location set to:', newPost.location, '| from:', {
        postDataLocation: postData.location,
        postDataLga: postData.lga,
        userLocationCity: userLocation?.city
      });
      
      // Helper function to add post without duplicates
      const addPostToState = (prev: any[]) => {
        // Check if a very similar post already exists (within last 5 seconds with same content)
        const isDuplicate = prev.some(p => 
          p.description === newPost.description &&
          p.location === newPost.location &&
          p.type === newPost.type &&
          (p.timestamp === 'Just now' || p.id?.startsWith('local-'))
        );
        
        if (isDuplicate) {
          console.log('⚠️ Duplicate post detected, skipping addition');
          return prev;
        }
        
        return [newPost, ...prev];
      };
      
      // Add to appropriate state immediately
      if (postData.type === 'traffic') {
        console.log('🚗 Adding traffic report to state:', newPost);
        setTrafficReports(prev => {
          const updated = addPostToState(prev);
          console.log('✅ Traffic reports updated. Total:', updated.length);
          return updated;
        });
      } else if (postData.type === 'event' || postData.type === 'crime' || postData.type === 'bus' || postData.type === 'activity') {
        console.log(`➕ Adding ${postData.type} to events state:`, newPost);
        setEvents(prev => {
          const updated = addPostToState(prev);
          console.log(`✅ Events updated. Total: ${updated.length} (type: ${postData.type})`);
          return updated;
        });
      } else if (postData.type === 'fuel') {
        console.log('⛽ Adding fuel update to state:', newPost);
        setFuelUpdates(prev => {
          const updated = addPostToState(prev);
          console.log('✅ Fuel updates updated. Total:', updated.length);
          return updated;
        });
      } else if (postData.type === 'market') {
        setMarketPrices(addPostToState);
        console.log('➕ Adding market price to state');
      } else if (postData.type === 'power') {
        setPowerReports(addPostToState);
        console.log('➕ Adding power report to state');
      } else if (postData.type === 'flood') {
        setFloodReports(addPostToState);
        console.log('➕ Adding flood report to state');
      } else if (postData.type === 'find') {
        setFindReports(addPostToState);
        console.log('➕ Adding find report to state');
      } else if (postData.type === 'nuisance') {
        setNuisanceReports(addPostToState);
        console.log('➕ Adding nuisance report to state');
      } else if (postData.type === 'water') {
        setWaterVendors(addPostToState);
        console.log('➕ Adding water vendor to state');
      }
      
      // Close modal
      setShowCreatePost(false);
      
      // Show immediate success feedback
      toast.success('Post Created!', {
        description: 'Your report is visible to neighbors now'
      });
      
      // Try to sync to backend
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        try {
          console.log('💾 Syncing to backend...');
          const response = await reportsService.createReport(newPost);
          
          if (response && response.id) {
            console.log('📡 Backend response:', response);
            console.log(`✅ Report synced to backend with ID: ${response.id}`);
            
            // Update the temp ID with the backend ID
            const updatePostId = (reports: any[]) => 
              reports.map(r => r.id === tempId ? { ...r, id: response.id } : r);
            
            if (postData.type === 'traffic') setTrafficReports(updatePostId);
            else if (postData.type === 'event') setEvents(updatePostId);
            else if (postData.type === 'fuel') setFuelUpdates(updatePostId);
            else if (postData.type === 'market') setMarketPrices(updatePostId);
            else if (postData.type === 'power') setPowerReports(updatePostId);
            else if (postData.type === 'flood') setFloodReports(updatePostId);
            else if (postData.type === 'find') setFindReports(updatePostId);
            else if (postData.type === 'nuisance') setNuisanceReports(updatePostId);
            else if (postData.type === 'water') setWaterVendors(updatePostId);
            
            console.log('✅ Post updated with backend ID');
            
            toast.success('Post Synced to Cloud!', {
              description: 'Your report is now visible to everyone'
            });
            
            // Force a backend sync to refresh the feed with the new post
            console.log('🔄 Forcing backend sync to refresh feed...');
            setTimeout(() => {
              syncWithBackend();
            }, 500);
          }
        } catch (error: any) {
          // Silently handle network errors
          if (error?.message?.includes('Failed to fetch') || 
              error?.message?.includes('NetworkError') ||
              error?.message?.includes('network')) {
            console.log('📡 Backend unavailable - post saved locally with ID:', tempId);
            toast.info('Post Saved Locally', {
              description: 'Will sync when connection is restored'
            });
          } else {
            console.error('⚠️ Backend sync failed:', error);
            toast.warning('Post Saved Locally', {
              description: 'Will sync when connection is restored'
            });
          }
        }
      } else {
        console.log('⚠️ No active session - skipping backend sync. Post saved locally with temp ID:', tempId);
        toast.info('Post Saved Locally', {
          description: 'Sign in to sync across devices'
        });
      }
      
    } catch (error) {
      console.error('❌ Error creating post:', error);
      toast.error('Failed to create post. Please try again.');
    }
  };

  // Admin: Handle bulk post deletion
  const handleBulkDeletePosts = async () => {
    if (selectedPostIds.size === 0) {
      toast.warning('No posts selected', { description: 'Please select posts to delete' });
      return;
    }

    const confirmed = confirm(`Are you sure you want to delete ${selectedPostIds.size} post(s)? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      console.log('🗑️ Admin deleting posts:', Array.from(selectedPostIds));
      
      // Remove from all state arrays
      const deleteFromArray = (posts: any[]) => posts.filter(p => !selectedPostIds.has(p.id));
      
      setTrafficReports(prev => deleteFromArray(prev));
      setEvents(prev => deleteFromArray(prev));
      setFuelUpdates(prev => deleteFromArray(prev));
      setMarketPrices(prev => deleteFromArray(prev));
      setPowerReports(prev => deleteFromArray(prev));
      setFloodReports(prev => deleteFromArray(prev));
      setFindReports(prev => deleteFromArray(prev));
      setNuisanceReports(prev => deleteFromArray(prev));
      setWaterVendors(prev => deleteFromArray(prev));
      
      // Try to delete from backend if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        try {
          for (const postId of selectedPostIds) {
            await reportsService.deleteReport(postId);
          }
          console.log('✅ Posts deleted from backend');
        } catch (error) {
          console.log('⚠️ Backend deletion failed, but posts removed locally');
        }
      }
      
      // Clear selection and exit delete mode
      setSelectedPostIds(new Set());
      setIsAdminDeleteMode(false);
      
      toast.success('Posts Deleted', {
        description: `${selectedPostIds.size} post(s) have been removed`,
        icon: '🗑️'
      });
      
    } catch (error) {
      console.error('❌ Error deleting posts:', error);
      toast.error('Failed to delete some posts. Please try again.');
    }
  };

  // Admin: Toggle post selection
  const handleTogglePostSelection = (postId: string) => {
    setSelectedPostIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  };

  // Admin: Select all posts
  const handleSelectAllPosts = () => {
    if (!allReports || allReports.length === 0) {
      toast.warning('No posts to select');
      return;
    }
    const allPostIds = new Set(allReports.map(r => r.id));
    setSelectedPostIds(allPostIds);
    toast.info('All posts selected', { description: `${allPostIds.size} posts selected` });
  };

  // Admin: Clear selection
  const handleClearSelection = () => {
    setSelectedPostIds(new Set());
    toast.info('Selection cleared');
  };

  // Handle authentication success from AuthModal
  const handleAuthSuccess = async (userData: any, isNewUser: boolean) => {
    try {
      console.log('✅ Auth success:', userData, 'isNewUser:', isNewUser);
      
      // Get the session from Supabase
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        await loadUserProfile(session.user.id);
        await recordDeviceLogin(session.user.id);
        
        if (isNewUser) {
          setShowWelcome(true);
          toast.success('🎉 Welcome to Sentlo!', {
            description: 'Your account has been created successfully'
          });
        } else {
          toast.success('Welcome back!');
        }
      } else {
        // Fallback: create a basic user object from the provided data
        setUser({
          id: 'temp-' + Date.now(),
          name: userData.name,
          email: userData.email,
          avatar: undefined,
          country: userData.country || 'Nigeria',
          role: 'user',
          trustScore: 0,
          coins: 0,
          createdAt: new Date().toISOString()
        });
        
        toast.success(isNewUser ? '🎉 Welcome to Sentlo!' : 'Welcome back!');
      }
      
      setShowAuthModal(false);
    } catch (error: any) {
      console.error('❌ Auth success handler error:', error);
    }
  };

  // Handle OTP verification
  const handleOTPVerify = async (otp: string) => {
    try {
      // Call backend signup with OTP
      const response = await authService.signup({
        email: pendingEmail,
        password: pendingPassword,
        name: pendingName,
        phone: '', // Add phone field
        country: detectedCountry
      });
      
      if (response?.session) {
        await loadUserProfile(response.user.id);
        setShowOTPModal(false);
        setShowWelcome(true);
        toast.success('Account created successfully!');
      }
    } catch (error: any) {
      console.error('❌ OTP verification failed:', error);
      toast.error(error.message || 'Invalid OTP code');
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('❌ Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setActiveView('search');
    addToHistory(query);
  };

  const addToHistory = (query: string) => {
    if (!query.trim()) return;
    setSearchHistory(prev => {
      const newHistory = [query, ...prev.filter(q => q !== query)];
      return newHistory.slice(0, 10); // Keep last 10 searches
    });
  };

  // Handle location click
  const handleLocationClick = (location: string) => {
    setSearchQuery(location);
    addToHistory(location);
    setActiveView('community');
    setActiveTab('traffic');
    toast.success(`📍 Showing updates for ${location}`, {
      description: 'Scroll down to see results'
    });
  };

  // Handle category change
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setActiveView('community');
    
    // Map category to tab
    const categoryTabMap: Record<string, TabType> = {
      'traffic': 'traffic',
      'crime': 'nuisance',
      'fuel': 'fuel',
      'power': 'power',
      'market': 'market',
      'weather': 'events'
    };
    
    if (category !== 'all' && categoryTabMap[category]) {
      setActiveTab(categoryTabMap[category]);
    }
    
    const categoryLabels: Record<string, string> = {
      'all': 'All Reports',
      'traffic': 'Traffic Reports',
      'crime': 'Crime & Safety',
      'fuel': 'Fuel Updates',
      'power': 'Power Updates',
      'market': 'Market Prices',
      'weather': 'Weather Updates'
    };
    
    toast.success(`🔍 Filtered by ${categoryLabels[category] || category}`);
  };

  // Handle report click
  const handleReportClick = (report: Report) => {
    setSelectedReport(report);
    setShowReportDetail(true);
  };

  // Handle edit post
  const handleEditPost = (report: Report) => {
    setSelectedReport(report);
    setShowEditPost(true);
  };

  // Handle update post
  const handleUpdatePost = async (updatedPost: any) => {
    try {
      const response = await reportsService.updateReport(updatedPost.id, updatedPost);
      
      if (response) {
        // Update in local state
        const updatePost = (reports: any[]) => 
          reports.map(r => r.id === updatedPost.id ? { ...r, ...updatedPost } : r);
        
        if (updatedPost.type === 'traffic') setTrafficReports(updatePost);
        else if (updatedPost.type === 'event') setEvents(updatePost);
        else if (updatedPost.type === 'fuel') setFuelUpdates(updatePost);
        else if (updatedPost.type === 'market') setMarketPrices(updatePost);
        else if (updatedPost.type === 'power') setPowerReports(updatePost);
        else if (updatedPost.type === 'flood') setFloodReports(updatePost);
        else if (updatedPost.type === 'find') setFindReports(updatePost);
        else if (updatedPost.type === 'nuisance') setNuisanceReports(updatePost);
        else if (updatedPost.type === 'water') setWaterVendors(updatePost);
        
        setShowEditPost(false);
        toast.success('Post updated successfully!');
      }
    } catch (error) {
      console.error('❌ Failed to update post:', error);
      toast.error('Failed to update post');
    }
  };

  // Handle delete post
  const handleDeletePost = async (reportId: string) => {
    try {
      await reportsService.deleteReport(reportId);
      
      // Remove from local state
      const removePost = (reports: any[]) => reports.filter(r => r.id !== reportId);
      
      setTrafficReports(removePost);
      setEvents(removePost);
      setFuelUpdates(removePost);
      setMarketPrices(removePost);
      setPowerReports(removePost);
      setFloodReports(removePost);
      setFindReports(removePost);
      setNuisanceReports(removePost);
      setWaterVendors(removePost);
      
      setShowReportDetail(false);
      setShowEditPost(false);
      toast.success('Post deleted successfully!');
    } catch (error) {
      console.error('❌ Failed to delete post:', error);
      toast.error('Failed to delete post');
    }
  };

  // Handle share
  const handleShare = (item: any) => {
    setItemToShare(item);
    setShowShareModal(true);
  };

  // Handle repost
  const handleRepostClick = (postId: string) => {
    const post = allReports.find(r => r.id === postId);
    if (post) {
      setRepostingPost(post);
      setShowRepostModal(true);
    }
  };

  const handleRepostSubmit = async (postId: string, note?: string) => {
    if (!user) {
      toast.error('Please log in to repost');
      return;
    }

    try {
      // Find the original post
      const originalPost = allReports.find(r => r.id === postId);
      if (!originalPost) return;

      // Create repost object
      const repost = {
        ...originalPost,
        id: `repost-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        isRepost: true,
        originalPostId: originalPost.id,
        originalAuthor: {
          userId: originalPost.userId,
          userName: originalPost.userName,
        },
        repostNote: note,
        userId: user.id,
        userName: user.name,
        timestamp: 'Just now',
        likes: 0,
        comments: [],
        repostCount: 0
      };

      // Update the original post's repost count
      const updatedReports = allReports.map(r => 
        r.id === originalPost.id 
          ? { ...r, repostCount: (r.repostCount || 0) + 1 }
          : r
      );

      // Add repost to the feed
      setAllReports([repost, ...updatedReports]);

      // Send to backend if available
      if (user) {
        try {
          await reportsService.createRepost(postId, note);
        } catch (error) {
          console.log('Backend repost failed, but local repost succeeded');
        }
      }

      setShowRepostModal(false);
      setRepostingPost(null);
    } catch (error) {
      console.error('Repost error:', error);
      throw error;
    }
  };

  // Handle add comment
  const handleAddComment = useCallback(async (postId: string, text: string) => {
    if (!user) {
      toast.error('Please log in to comment');
      return;
    }

    try {
      const newComment = {
        id: `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        user: user.name,
        text: text,
        timestamp: 'Just now',
        replies: []
      };

      // Update all reports arrays
      const updateComments = (reports: any[]) =>
        reports.map(r =>
          r.id === postId
            ? { ...r, comments: [...(r.comments || []), newComment] }
            : r
        );

      setAllReports(prev => updateComments(prev));
      setTrafficReports(prev => updateComments(prev));
      setEvents(prev => updateComments(prev));
      setFuelUpdates(prev => updateComments(prev));
      setMarketPrices(prev => updateComments(prev));
      setPowerReports(prev => updateComments(prev));
      setFloodReports(prev => updateComments(prev));
      setFindReports(prev => updateComments(prev));
      setNuisanceReports(prev => updateComments(prev));
      setWaterVendors(prev => updateComments(prev));

      // Update selectedReport if it's the one being commented on
      setSelectedReport(prev => {
        if (prev?.id === postId) {
          return {
            ...prev,
            comments: [...(prev.comments || []), newComment]
          };
        }
        return prev;
      });

      toast.success('Comment added!');

      // Send to backend if available
      try {
        await reportsService.addComment(postId, text);
      } catch (error) {
        console.log('Backend comment failed, but local comment succeeded');
      }
    } catch (error) {
      console.error('Add comment error:', error);
      toast.error('Failed to add comment');
    }
  }, [user]);

  // Handle user profile view
  const handleViewProfile = (userId: string) => {
    setSelectedUserId(userId);
    setShowUserProfile(true);
  };

  // Filter reports by search query
  const filteredReports = React.useMemo(() => {
    if (!searchQuery.trim()) return allReports;
    
    const query = searchQuery.toLowerCase();
    const filtered = allReports.filter(report => 
      report.location?.toLowerCase().includes(query) ||
      report.content?.toLowerCase().includes(query) ||
      report.userName?.toLowerCase().includes(query) ||
      report.region?.toLowerCase().includes(query) || // Search by state/region
      report.country?.toLowerCase().includes(query) // Search by country
    );
    
    console.log(`🔍 Search filter "${query}": ${allReports.length} total → ${filtered.length} matching`);
    
    return filtered;
  }, [allReports, searchQuery]);

  // Handle onboarding complete
  const handleOnboardingComplete = () => {
    localStorage.setItem('sentlo_has_seen_onboarding', 'true');
    setShowOnboarding(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-zinc-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-600 font-medium">Loading Sentlo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <Logo />
            <div className="flex items-center gap-3">
              {/* Install App Button - visible to all users */}
              <div className="hidden sm:block">
                <InstallAppButton />
              </div>
              
              {user && (
                <button
                  onClick={() => setShowNotifications(true)}
                  className="p-2 text-zinc-600 hover:bg-zinc-100 rounded-lg relative"
                >
                  <Bell className="w-5 h-5" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                </button>
              )}
              {!user && (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
          
          <LocationSearchHeader
            onSearch={handleSearch}
            onLocationClick={handleLocationClick}
            searchQuery={searchQuery}
            searchHistory={searchHistory}
            onCategoryChange={handleCategoryChange}
            selectedCategory={selectedCategory}
          />
        </div>
      </header>

      {/* Advertisement Banner */}
      <AdvertBanner />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {activeView === 'home' && (
          <div>
            {/* Welcome Banner */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg p-6 mb-6 text-white">
              <h2 className="text-2xl font-bold mb-2">Welcome to Sentlo!</h2>
              <p className="text-green-50">Nigeria's hyperlocal community network</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg p-4 border border-zinc-200">
                <div className="text-2xl font-bold text-green-600">{allReports.length}</div>
                <div className="text-xs text-zinc-600">Reports</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-zinc-200">
                <div className="text-2xl font-bold text-blue-600">{events.length}</div>
                <div className="text-xs text-zinc-600">Events</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-zinc-200">
                <div className="text-2xl font-bold text-amber-600">{user?.coins || 0}</div>
                <div className="text-xs text-zinc-600">Coins</div>
              </div>
            </div>

            {/* Recent Activity Feed */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-zinc-900">Recent Updates</h3>
              {allReports.slice(0, 5).map((report: any) => (
                <ReportCard 
                  key={report.id} 
                  report={report}
                  currentUserName={user?.name}
                  currentUserId={user?.id}
                  isAdminDeleteMode={isAdminDeleteMode}
                  isSelected={selectedPostIds.has(report.id)}
                  onToggleSelect={handleTogglePostSelection}
                  onRepost={handleRepostClick}
                  onComment={() => handleReportClick(report)}
                />
              ))}
            </div>
          </div>
        )}

        {activeView === 'community' && (
          <div>
            {/* Admin Controls Toolbar */}
            {user?.role === 'admin' && (
              <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-5 h-5 text-red-600" />
                    <span className="font-bold text-red-900">Admin Mode</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {!isAdminDeleteMode ? (
                      <button
                        onClick={() => setIsAdminDeleteMode(true)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Enable Delete Mode
                      </button>
                    ) : (
                      <>
                        <span className="text-sm text-red-700 font-medium">
                          {selectedPostIds.size} selected
                        </span>
                        <button
                          onClick={handleSelectAllPosts}
                          className="px-3 py-2 bg-white text-red-600 border border-red-300 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors"
                        >
                          Select All
                        </button>
                        <button
                          onClick={handleClearSelection}
                          className="px-3 py-2 bg-white text-zinc-600 border border-zinc-300 rounded-lg text-sm font-medium hover:bg-zinc-50 transition-colors"
                        >
                          Clear
                        </button>
                        <button
                          onClick={handleBulkDeletePosts}
                          disabled={selectedPostIds.size === 0}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete ({selectedPostIds.size})
                        </button>
                        <button
                          onClick={() => {
                            setIsAdminDeleteMode(false);
                            setSelectedPostIds(new Set());
                          }}
                          className="px-3 py-2 bg-zinc-600 text-white rounded-lg text-sm font-medium hover:bg-zinc-700 transition-colors"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2 hide-scrollbar">
              <button
                onClick={() => setActiveTab('traffic')}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'traffic'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-zinc-600 hover:bg-zinc-100'
                }`}
              >
                🚗 Traffic
              </button>
              <button
                onClick={() => setActiveTab('events')}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'events'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-zinc-600 hover:bg-zinc-100'
                }`}
              >
                📅 Events
              </button>
              <button
                onClick={() => setActiveTab('fuel')}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'fuel'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-zinc-600 hover:bg-zinc-100'
                }`}
              >
                ⛽ Fuel
              </button>
              <button
                onClick={() => setActiveTab('market')}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'market'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-zinc-600 hover:bg-zinc-100'
                }`}
              >
                🛒 Market
              </button>
              <button
                onClick={() => setActiveTab('power')}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'power'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-zinc-600 hover:bg-zinc-100'
                }`}
              >
                ⚡ Power
              </button>
              <button
                onClick={() => setActiveTab('flood')}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  activeTab === 'flood'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-zinc-600 hover:bg-zinc-100'
                }`}
              >
                🌊 Flood
              </button>
            </div>

            {/* IMPROVEMENT 3: Active Search Filter Banner */}
            {searchQuery && (
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-6 animate-in slide-in-from-top">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Search className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-blue-900 text-sm">
                        Showing posts from: "{searchQuery}"
                      </p>
                      <p className="text-xs text-blue-700 mt-0.5">
                        {filteredReports.length} {filteredReports.length === 1 ? 'post' : 'posts'} found
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-colors flex items-center gap-1.5 flex-shrink-0"
                  >
                    <X className="w-3.5 h-3.5" />
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Tab Content */}
            {activeTab === 'traffic' && (
              <div className="space-y-4">
                {trafficReports.filter(r => 
                  !searchQuery || r.location?.toLowerCase().includes(searchQuery.toLowerCase())
                ).length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <p>No traffic reports yet. Be the first to share!</p>
                  </div>
                ) : (
                  trafficReports.filter(r => 
                    !searchQuery || r.location?.toLowerCase().includes(searchQuery.toLowerCase())
                  ).map((report: any) => (
                    <ReportCard 
                      key={report.id} 
                      report={report}
                      currentUserName={user?.name}
                      currentUserId={user?.id}
                      isAdminDeleteMode={isAdminDeleteMode}
                      isSelected={selectedPostIds.has(report.id)}
                      onToggleSelect={handleTogglePostSelection}
                      onRepost={handleRepostClick}
                      onComment={() => handleReportClick(report)}
                    />
                  ))
                )}
              </div>
            )}
            
            {activeTab === 'events' && (
              <div className="space-y-4">
                {events.filter(e => 
                  !searchQuery || e.location?.toLowerCase().includes(searchQuery.toLowerCase())
                ).length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <p>No events yet. Be the first to post!</p>
                  </div>
                ) : (
                  events.filter(e => 
                    !searchQuery || e.location?.toLowerCase().includes(searchQuery.toLowerCase())
                  ).map((event: any) => (
                    <ReportCard 
                      key={event.id} 
                      report={event}
                      currentUserName={user?.name}
                      currentUserId={user?.id}
                      isAdminDeleteMode={isAdminDeleteMode}
                      isSelected={selectedPostIds.has(event.id)}
                      onToggleSelect={handleTogglePostSelection}
                      onRepost={handleRepostClick}
                      onComment={() => handleReportClick(event)}
                    />
                  ))
                )}
              </div>
            )}
            
            {activeTab === 'fuel' && (
              <div className="space-y-4">
                {fuelUpdates.filter(f => 
                  !searchQuery || f.location?.toLowerCase().includes(searchQuery.toLowerCase())
                ).length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <p>No fuel updates yet. Share prices!</p>
                  </div>
                ) : (
                  fuelUpdates.filter(f => 
                    !searchQuery || f.location?.toLowerCase().includes(searchQuery.toLowerCase())
                  ).map((fuel: any) => (
                    <ReportCard 
                      key={fuel.id} 
                      report={fuel}
                      currentUserName={user?.name}
                      currentUserId={user?.id}
                      isAdminDeleteMode={isAdminDeleteMode}
                      isSelected={selectedPostIds.has(fuel.id)}
                      onToggleSelect={handleTogglePostSelection}
                      onRepost={handleRepostClick}
                      onComment={() => handleReportClick(fuel)}
                    />
                  ))
                )}
              </div>
            )}
            
            {activeTab === 'market' && (
              <div className="space-y-4">
                {marketPrices.filter(m => 
                  !searchQuery || m.location?.toLowerCase().includes(searchQuery.toLowerCase())
                ).length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <p>No market prices yet. Share what you see!</p>
                  </div>
                ) : (
                  marketPrices.filter(m => 
                    !searchQuery || m.location?.toLowerCase().includes(searchQuery.toLowerCase())
                  ).map((price: any) => (
                    <ReportCard 
                      key={price.id} 
                      report={price}
                      currentUserName={user?.name}
                      currentUserId={user?.id}
                      isAdminDeleteMode={isAdminDeleteMode}
                      isSelected={selectedPostIds.has(price.id)}
                      onToggleSelect={handleTogglePostSelection}
                      onRepost={handleRepostClick}
                      onComment={() => handleReportClick(price)}
                    />
                  ))
                )}
              </div>
            )}
            
            {activeTab === 'power' && (
              <div className="space-y-4">
                {powerReports.filter(p => 
                  !searchQuery || p.location?.toLowerCase().includes(searchQuery.toLowerCase())
                ).length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <p>No power reports yet. Share NEPA updates!</p>
                  </div>
                ) : (
                  powerReports.filter(p => 
                    !searchQuery || p.location?.toLowerCase().includes(searchQuery.toLowerCase())
                  ).map((power: any) => (
                    <ReportCard 
                      key={power.id} 
                      report={power}
                      currentUserName={user?.name}
                      currentUserId={user?.id}
                      isAdminDeleteMode={isAdminDeleteMode}
                      isSelected={selectedPostIds.has(power.id)}
                      onToggleSelect={handleTogglePostSelection}
                      onRepost={handleRepostClick}
                      onComment={() => handleReportClick(power)}
                    />
                  ))
                )}
              </div>
            )}
            
            {activeTab === 'flood' && (
              <div className="space-y-4">
                {floodReports.filter(f => 
                  !searchQuery || f.location?.toLowerCase().includes(searchQuery.toLowerCase())
                ).length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <p>No flood reports yet.</p>
                  </div>
                ) : (
                  floodReports.filter(f => 
                    !searchQuery || f.location?.toLowerCase().includes(searchQuery.toLowerCase())
                  ).map((flood: any) => (
                    <ReportCard 
                      key={flood.id} 
                      report={flood}
                      currentUserName={user?.name}
                      currentUserId={user?.id}
                      isAdminDeleteMode={isAdminDeleteMode}
                      isSelected={selectedPostIds.has(flood.id)}
                      onToggleSelect={handleTogglePostSelection}
                      onRepost={handleRepostClick}
                      onComment={() => handleReportClick(flood)}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {activeView === 'feature' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-zinc-900">Features</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <button
                onClick={() => setShowPowerTracker(true)}
                className="p-6 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 transition-colors text-left"
              >
                <Zap className="w-8 h-8 text-amber-500 mb-3" />
                <h3 className="font-bold text-zinc-900 mb-1">Light Watch</h3>
                <p className="text-sm text-zinc-600">Track power status in your area</p>
              </button>
              
              {/* Digital Ajo - Only show if feature is enabled */}
              {isAjoEnabled && (
                <button
                  onClick={() => setShowAjoSavings(true)}
                  className="p-6 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 transition-colors text-left"
                >
                  <Coins className="w-8 h-8 text-emerald-500 mb-3" />
                  <h3 className="font-bold text-zinc-900 mb-1">Digital Ajo</h3>
                  <p className="text-sm text-zinc-600">Join savings circles</p>
                </button>
              )}
              
              <button
                onClick={() => setShowTransportHub(true)}
                className="p-6 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 transition-colors text-left"
              >
                <Fuel className="w-8 h-8 text-blue-500 mb-3" />
                <h3 className="font-bold text-zinc-900 mb-1">Transport Hub</h3>
                <p className="text-sm text-zinc-600">Fuel prices & transport info</p>
              </button>
              
              <button
                onClick={() => setShowMarketplace(true)}
                className="p-6 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 transition-colors text-left"
              >
                <ShoppingBag className="w-8 h-8 text-purple-500 mb-3" />
                <h3 className="font-bold text-zinc-900 mb-1">Marketplace</h3>
                <p className="text-sm text-zinc-600">Buy & sell locally</p>
              </button>
              
              <button
                onClick={() => setShowMarketplace(true)}
                className="p-6 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 transition-colors text-left"
              >
                <ShieldCheck className="w-8 h-8 text-green-500 mb-3" />
                <h3 className="font-bold text-zinc-900 mb-1">SafePay</h3>
                <p className="text-sm text-zinc-600">Secure escrow service</p>
              </button>
              
              <button
                onClick={() => setShowBusStopWatch(true)}
                className="p-6 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 transition-colors text-left"
              >
                <Users className="w-8 h-8 text-orange-500 mb-3" />
                <h3 className="font-bold text-zinc-900 mb-1">Bus Stop Watch</h3>
                <p className="text-sm text-zinc-600">Track crowd levels</p>
              </button>
            </div>
          </div>
        )}

        {activeView === 'hub' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-zinc-900">More Features</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <button
                onClick={() => setShowPowerTracker(true)}
                className="p-6 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 transition-colors text-left"
              >
                <Zap className="w-8 h-8 text-amber-500 mb-3" />
                <h3 className="font-bold text-zinc-900 mb-1">Light Watch</h3>
                <p className="text-sm text-zinc-600">Track power status in your area</p>
              </button>
              
              {/* Digital Ajo - Only show if feature is enabled */}
              {isAjoEnabled && (
                <button
                  onClick={() => setShowAjoSavings(true)}
                  className="p-6 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 transition-colors text-left"
                >
                  <Coins className="w-8 h-8 text-emerald-500 mb-3" />
                  <h3 className="font-bold text-zinc-900 mb-1">Digital Ajo</h3>
                  <p className="text-sm text-zinc-600">Join savings circles</p>
                </button>
              )}
              
              <button
                onClick={() => setShowTransportHub(true)}
                className="p-6 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 transition-colors text-left"
              >
                <TrendingUp className="w-8 h-8 text-blue-500 mb-3" />
                <h3 className="font-bold text-zinc-900 mb-1">Transport Hub</h3>
                <p className="text-sm text-zinc-600">Find bus stops & routes</p>
              </button>
              
              <button
                onClick={() => setShowSwapShop(true)}
                className="p-6 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 transition-colors text-left"
              >
                <Users className="w-8 h-8 text-purple-500 mb-3" />
                <h3 className="font-bold text-zinc-900 mb-1">Swap & Shop</h3>
                <p className="text-sm text-zinc-600">Trade with neighbors</p>
              </button>
              
              <button
                onClick={() => setShowMarketplace(true)}
                className="p-6 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 transition-colors text-left"
              >
                <ShieldCheck className="w-8 h-8 text-green-500 mb-3" />
                <h3 className="font-bold text-zinc-900 mb-1">SafePay Escrow</h3>
                <p className="text-sm text-zinc-600">Secure transactions</p>
              </button>
              
              <button
                onClick={() => setShowBusStopWatch(true)}
                className="p-6 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 transition-colors text-left"
              >
                <Users className="w-8 h-8 text-orange-500 mb-3" />
                <h3 className="font-bold text-zinc-900 mb-1">Bus Stop Watch</h3>
                <p className="text-sm text-zinc-600">Track crowd levels</p>
              </button>
            </div>
          </div>
        )}

        {activeView === 'map' && (
          <div className="bg-white rounded-lg overflow-hidden border border-zinc-200">
            <GoogleMapView
              reports={filteredReports}
              onReportClick={handleReportClick}
            />
          </div>
        )}

        {activeView === 'profile' && (
          <div className="max-w-2xl mx-auto space-y-6">
            {user ? (
              <>
                {/* Profile Header */}
                <div className="bg-white rounded-lg p-6 border border-zinc-200">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-zinc-900">{user.name}</h2>
                      <p className="text-zinc-600">{user.email}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                          {user.role}
                        </span>
                        <span className="text-sm text-zinc-600">
                          ⭐ {user.trustScore} Trust Score
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-zinc-50 rounded-lg">
                      <div className="flex items-center gap-2 text-amber-600 mb-1">
                        <Coins className="w-5 h-5" />
                        <span className="font-bold">{user.coins}</span>
                      </div>
                      <p className="text-sm text-zinc-600">Sentlo Coins</p>
                    </div>
                    <div className="p-4 bg-zinc-50 rounded-lg">
                      <div className="flex items-center gap-2 text-blue-600 mb-1">
                        <Trophy className="w-5 h-5" />
                        <span className="font-bold">{allReports.filter(r => r.userId === user.id).length}</span>
                      </div>
                      <p className="text-sm text-zinc-600">Reports Shared</p>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => setShowMyPosts(true)}
                    className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <MessageCircle className="w-6 h-6 text-emerald-600" />
                      <div>
                        <h3 className="font-bold text-zinc-900">My Posts</h3>
                        <p className="text-sm text-zinc-600">View and manage your reports</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setShowReferralDashboard(true)}
                    className="p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <Gift className="w-6 h-6 text-blue-600" />
                      <div>
                        <h3 className="font-bold text-zinc-900">Referrals</h3>
                        <p className="text-sm text-zinc-600">Invite friends and earn</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setShowSettings(true)}
                    className="p-4 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <Settings className="w-6 h-6 text-zinc-600" />
                      <div>
                        <h3 className="font-bold text-zinc-900">Settings</h3>
                        <p className="text-sm text-zinc-600">Account preferences</p>
                      </div>
                    </div>
                  </button>
                  
                  {/* Install App Button */}
                  <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-zinc-900">Install Sentlo</h3>
                        <p className="text-sm text-zinc-600">Add to home screen</p>
                      </div>
                    </div>
                    <InstallAppButton />
                  </div>
                  
                  {user.role === 'admin' && (
                    <button
                      onClick={() => setShowAdminPanel(true)}
                      className="p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <ShieldCheck className="w-6 h-6 text-red-600" />
                        <div>
                          <h3 className="font-bold text-zinc-900">Admin Panel</h3>
                          <p className="text-sm text-zinc-600">Manage platform</p>
                        </div>
                      </div>
                    </button>
                  )}
                </div>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="w-full p-4 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors text-zinc-700 font-medium flex items-center justify-center gap-2"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </>
            ) : (
              <div className="bg-white rounded-lg p-12 text-center border border-zinc-200">
                <UserCircle className="w-16 h-16 text-zinc-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-zinc-900 mb-2">Sign In Required</h2>
                <p className="text-zinc-600 mb-6">
                  Sign in to access your profile, track your posts, and earn rewards
                </p>
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                >
                  Sign In / Sign Up
                </button>
              </div>
            )}
          </div>
        )}

        {activeView === 'search' && (
          <div>
            <h2 className="text-2xl font-bold text-zinc-900 mb-6">
              Search Results for "{searchQuery}"
            </h2>
            {filteredReports.length > 0 ? (
              <div className="space-y-4">
                {filteredReports.map(report => (
                  <div
                    key={report.id}
                    onClick={() => handleReportClick(report)}
                    className="p-4 bg-white rounded-lg border border-zinc-200 hover:border-emerald-500 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{report.type === 'traffic' ? '🚗' : report.type === 'event' ? '📅' : '📢'}</span>
                        <div>
                          <h3 className="font-bold text-zinc-900">
                            {report.location}
                            {report.region && <span className="text-zinc-500 font-normal">, {report.region}</span>}
                          </h3>
                          <p className="text-sm text-zinc-600">{report.userName} • {report.timestamp}</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-zinc-700">{report.description || report.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg p-12 text-center border border-zinc-200">
                <Search className="w-16 h-16 text-zinc-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-zinc-900 mb-2">No Results Found</h3>
                <p className="text-zinc-600">Try a different search term or location</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Floating Create Post Button */}
      <button
        onClick={() => user ? setShowCreatePost(true) : setShowAuthModal(true)}
        className="fixed bottom-24 right-6 w-16 h-16 bg-emerald-600 text-white rounded-full shadow-lg hover:bg-emerald-700 transition-all hover:scale-110 z-30 flex items-center justify-center"
      >
        <Plus className="w-8 h-8" />
      </button>

      {/* Help Button */}
      <HelpButton onClick={() => setShowHowItWorks(true)} />

      {/* Floating Feature Menu */}
      <FloatingFeatureMenu
        onFeatureClick={(feature) => {
          if (feature === 'power') setShowPowerTracker(true);
          else if (feature === 'ajo' && isAjoEnabled) setShowAjoSavings(true);
          else if (feature === 'transport') setShowTransportHub(true);
          else if (feature === 'marketplace') setShowMarketplace(true);
          else if (feature === 'safepay') setShowMarketplace(true);
          else if (feature === 'buswatch') setShowBusStopWatch(true);
          else if (feature === 'voice') setShowVoiceReporter(true);
        }}
        isAjoEnabled={isAjoEnabled}
      />

      {/* Bottom Navigation */}
      <BottomNav
        activeView={activeView}
        onViewChange={setActiveView}
        onReportClick={() => user ? setShowCreatePost(true) : setShowAuthModal(true)}
      />

      {/* Modals */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />

      <CreatePostModal
        isOpen={showCreatePost}
        onClose={() => setShowCreatePost(false)}
        onSubmit={handleCreatePost}
        user={user}
      />

      <OTPModal
        isOpen={showOTPModal}
        onClose={() => setShowOTPModal(false)}
        onVerify={handleOTPVerify}
        email={pendingEmail}
      />

      <HowItWorksModal
        isOpen={showHowItWorks}
        onClose={() => setShowHowItWorks(false)}
      />

      <WelcomeModal
        isOpen={showWelcome}
        onClose={() => setShowWelcome(false)}
        userName={user?.name || ''}
      />

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        user={user}
        onUpdateUser={setUser}
        preferences={preferences}
        onUpdatePreferences={setPreferences}
      />

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        item={itemToShare}
      />

      <SimpleOnboarding
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={handleOnboardingComplete}
      />

      <SOSModal
        isOpen={showSOSModal}
        onClose={() => setShowSOSModal(false)}
      />

      {showAdminPanel && (
        <AdminControlPanel
          isOpen={showAdminPanel}
          onClose={() => setShowAdminPanel(false)}
          user={user}
        />
      )}

      {showNotifications && (
        <NotificationCenter
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          user={user}
        />
      )}

      {showReportDetail && selectedReport && (
        <ReportDetailModal
          isOpen={showReportDetail}
          onClose={() => setShowReportDetail(false)}
          report={selectedReport}
          onEdit={() => handleEditPost(selectedReport)}
          onDelete={() => handleDeletePost(selectedReport.id)}
          onShare={() => handleShare(selectedReport)}
          currentUser={user}
          onAddComment={handleAddComment}
        />
      )}

      {showEditPost && selectedReport && (
        <EditPostModal
          isOpen={showEditPost}
          onClose={() => setShowEditPost(false)}
          report={selectedReport}
          onUpdate={handleUpdatePost}
        />
      )}

      <RepostModal
        isOpen={showRepostModal}
        onClose={() => {
          setShowRepostModal(false);
          setRepostingPost(null);
        }}
        post={repostingPost}
        onRepost={handleRepostSubmit}
        currentUserName={user?.name}
      />

      {showMyPosts && (
        <MyPostsView
          isOpen={showMyPosts}
          onClose={() => setShowMyPosts(false)}
          user={user}
          onEditPost={handleEditPost}
          onDeletePost={handleDeletePost}
          onSharePost={handleShare}
        />
      )}

      {showUserProfile && selectedUserId && (
        <UserProfileView
          isOpen={showUserProfile}
          onClose={() => setShowUserProfile(false)}
          userId={selectedUserId}
        />
      )}

      {showReferralDashboard && (
        <ReferralDashboard
          isOpen={showReferralDashboard}
          onClose={() => setShowReferralDashboard(false)}
          user={user}
        />
      )}

      {/* Feature Modals */}
      {showPowerTracker && (
        <LightWatchWidget
          isOpen={showPowerTracker}
          onClose={() => setShowPowerTracker(false)}
          reports={powerReports}
          onReportClick={handleReportClick}
          onShare={handleShare}
        />
      )}

      {/* Digital Ajo - Only render if feature is enabled */}
      {showAjoSavings && isAjoEnabled && (
        <AjoSavingsWidget
          isOpen={showAjoSavings}
          onClose={() => setShowAjoSavings(false)}
          user={user}
        />
      )}

      {showTransportHub && (
        <TransportFuelHub
          isOpen={showTransportHub}
          onClose={() => setShowTransportHub(false)}
          fuelUpdates={fuelUpdates}
          onReportClick={handleReportClick}
          onShare={handleShare}
        />
      )}

      {showMarketplace && (
        <NeighborhoodMarketplace
          isOpen={showMarketplace}
          onClose={() => setShowMarketplace(false)}
          user={user}
        />
      )}

      {showBusStopWatch && (
        <BusStopCrowdWatch
          isOpen={showBusStopWatch}
          onClose={() => setShowBusStopWatch(false)}
          onReportClick={handleReportClick}
          onShare={handleShare}
        />
      )}

      {showVoiceReporter && (
        <VoiceReporter
          isOpen={showVoiceReporter}
          onClose={() => setShowVoiceReporter(false)}
          onSubmit={handleCreatePost}
          user={user}
        />
      )}

      {showPartnershipHub && (
        <PartnershipHub
          isOpen={showPartnershipHub}
          onClose={() => setShowPartnershipHub(false)}
          user={user}
        />
      )}

      {showSecurityCenter && (
        <SecurityCenter
          isOpen={showSecurityCenter}
          onClose={() => setShowSecurityCenter(false)}
          user={user}
        />
      )}

      {/* PWA Components */}
      <VersionManager />
      <OfflineIndicator />
      <PWAUpdateNotification />
      <PWAInstallPrompt />
      <AppHealthCheck />

      {/* Toast Notifications */}
      <Toaster position="top-center" richColors />
    </div>
  );
}
